import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  FaArrowLeft,
  FaShieldAlt,
  FaCogs,
  FaAward,
  FaUsers,
  FaIndustry,
  FaMicrochip,
  FaCheckCircle,
  FaBoxes,
  FaTruck,
  FaHandshake,
  FaQuoteLeft,
  FaQuoteRight,
  FaBuilding,
  FaGlobe,
  FaChartLine,
  FaLeaf,
  FaBullseye,
  FaUserFriends,
  FaHistory,
  FaSearch,
  FaChessKnight,
  FaCog,
  FaRocket,
  FaLightbulb,
  FaBullhorn,
  FaLaptopCode,
  FaChartBar,
  FaShoppingCart,
  FaFileInvoiceDollar,
  FaWarehouse,
  FaUserTie,
  FaArrowRight,
  FaCheck
} from 'react-icons/fa';

import ashrafiaLogo from '../logos/ashraftechnology/ashraftechnologylogo.png';
import heroImage from '../logos/ashraftechnology/ashraftechnology1.png';

export default function AshrafiaTechnologies() {
  const navigate = useNavigate();

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('show');
          }
        });
      },
      {
        threshold: 0.12
      }
    );

    const elements = document.querySelectorAll('.reveal');

    elements.forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <>
      <style>{`

        * {
          box-sizing: border-box;
        }

        html {
          scroll-behavior: smooth;
        }

        body {
          margin: 0;
          padding: 0;
          overflow-x: hidden;
          font-family: Arial, Helvetica, sans-serif;
          background: #061321;
        }

        img {
          max-width: 100%;
        }

        button {
          font-family: inherit;
        }

        /* =========================================================
           PAGE
        ========================================================= */

        .tech-page {
          width: 100%;
          min-height: 100vh;
          background:
            radial-gradient(
              circle at 80% 10%,
              rgba(0, 174, 239, 0.12),
              transparent 30%
            ),
            radial-gradient(
              circle at 10% 50%,
              rgba(255, 111, 37, 0.07),
              transparent 30%
            ),
            #061321;
          color: white;
          overflow: hidden;
        }

        /* =========================================================
           BACK BUTTON
        ========================================================= */

        .top-navigation {
          position: fixed;
          top: 18px;
          left: 22px;
          z-index: 999;
        }

        .back-button {
          display: flex;
          align-items: center;
          gap: 9px;
          border: 1px solid rgba(0, 197, 239, 0.5);
          background: rgba(4, 20, 36, 0.88);
          backdrop-filter: blur(12px);
          color: white;
          padding: 11px 18px;
          border-radius: 30px;
          cursor: pointer;
          font-size: 13px;
          font-weight: 700;
          transition: all 0.35s ease;
          box-shadow: 0 10px 30px rgba(0,0,0,0.25);
        }

        .back-button:hover {
          background: #00a9d7;
          color: #061321;
          transform: translateX(5px);
          border-color: #00d5ff;
        }

        /* =========================================================
           COMMON
        ========================================================= */

        .section-container {
          width: 100%;
          padding: 80px 5vw;
        }

        .section-inner {
          width: 100%;
          max-width: 1450px;
          margin: auto;
        }

        .section-heading {
          text-align: center;
          margin-bottom: 55px;
        }

        .section-heading-small {
          color: #ff7b32;
          font-size: 13px;
          font-weight: 800;
          letter-spacing: 3px;
          text-transform: uppercase;
          margin-bottom: 10px;
        }

        .section-heading h2 {
          margin: 0;
          font-size: clamp(28px, 4vw, 48px);
          font-weight: 900;
          letter-spacing: 1px;
          text-transform: uppercase;
        }

        .section-heading h2 span {
          color: #00b9e8;
        }

        .section-heading p {
          max-width: 760px;
          margin: 18px auto 0;
          color: #9eb3c5;
          line-height: 1.7;
          font-size: 15px;
        }

        .cyan {
          color: #00c0ed;
        }

        .orange {
          color: #ff792d;
        }

        /* =========================================================
           REVEAL ANIMATION
        ========================================================= */

        .reveal {
          opacity: 0;
          transform: translateY(55px);
          transition:
            opacity 0.8s ease,
            transform 0.8s cubic-bezier(.22,.61,.36,1);
        }

        .reveal.show {
          opacity: 1;
          transform: translateY(0);
        }

        .reveal-left {
          opacity: 0;
          transform: translateX(-60px);
          transition:
            opacity 0.9s ease,
            transform 0.9s ease;
        }

        .reveal-left.show {
          opacity: 1;
          transform: translateX(0);
        }

        .reveal-right {
          opacity: 0;
          transform: translateX(60px);
          transition:
            opacity 0.9s ease,
            transform 0.9s ease;
        }

        .reveal-right.show {
          opacity: 1;
          transform: translateX(0);
        }

        /* =========================================================
           HERO
        ========================================================= */

        .hero {
          width: 100%;
          min-height: 100vh;
          position: relative;
          display: flex;
          align-items: center;
          overflow: hidden;
          background:
            linear-gradient(
              90deg,
              #061321 0%,
              #071b2d 43%,
              rgba(5, 24, 40, 0.7) 58%,
              rgba(4, 20, 34, 0.2) 100%
            );
        }

        .hero::before {
          content: "";
          position: absolute;
          width: 600px;
          height: 600px;
          border-radius: 50%;
          background: rgba(0, 188, 235, 0.07);
          filter: blur(20px);
          top: -250px;
          right: -150px;
          animation: floatGlow 8s ease-in-out infinite;
        }

        .hero::after {
          content: "";
          position: absolute;
          width: 400px;
          height: 400px;
          border-radius: 50%;
          background: rgba(255, 110, 40, 0.05);
          filter: blur(30px);
          bottom: -200px;
          left: -150px;
        }

        .hero-content {
          width: 100%;
          max-width: 1450px;
          margin: auto;
          padding: 100px 5vw 70px;
          display: grid;
          grid-template-columns: 45% 55%;
          align-items: center;
          position: relative;
          z-index: 2;
        }

        .hero-left {
          padding-right: 45px;
          animation: heroText 1s ease forwards;
        }

        /* =========================================================
           LOGO
        ========================================================= */

        .logo-wrapper {
          width: min(380px, 80%);
          margin-bottom: 8px;
        }

        .logo-wrapper img {
          width: 100%;
          height: auto;
          object-fit: contain;
          display: block;
          filter:
            drop-shadow(0 0 15px rgba(0,190,240,0.15));
          transition: transform 0.5s ease;
        }

        .logo-wrapper img:hover {
          transform: scale(1.04);
        }

        /* =========================================================
           ASHRAF TECHNOLOGIES TEXT BELOW LOGO
        ========================================================= */

        .technology-name {
          margin-top: 4px;
          margin-bottom: 28px;
          padding-left: 3px;
          font-size: 18px;
          font-weight: 900;
          letter-spacing: 4px;
          text-transform: uppercase;
          line-height: 1.2;
          color: #ffffff;
        }

        .technology-name span {
          color: #00bce9;
        }

        .technology-name::after {
          content: "";
          display: block;
          width: 65px;
          height: 3px;
          margin-top: 10px;
          background: linear-gradient(
            90deg,
            #00bce9,
            #ff772d
          );
          border-radius: 5px;
        }

        .hero-kicker {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          color: #00bce8;
          font-size: 14px;
          font-weight: 800;
          letter-spacing: 2px;
          margin-bottom: 18px;
          text-transform: uppercase;
        }

        .hero-kicker::before {
          content: "";
          width: 35px;
          height: 2px;
          background: #ff762d;
        }

        .hero-title {
          margin: 0;
          font-size: clamp(38px, 5vw, 74px);
          line-height: 0.98;
          font-weight: 900;
          text-transform: uppercase;
          letter-spacing: -1px;
        }

        .hero-title .blue {
          color: #00bce9;
          display: block;
        }

        .hero-title .orange {
          color: #ff772d;
        }

        .hero-description {
          margin-top: 25px;
          max-width: 620px;
          color: #c4d4df;
          line-height: 1.8;
          font-size: 16px;
        }

        .hero-description strong {
          color: #ff7b32;
        }

        .hero-buttons {
          display: flex;
          gap: 14px;
          margin-top: 30px;
          flex-wrap: wrap;
        }

        .primary-button,
        .secondary-button {
          padding: 14px 23px;
          border-radius: 5px;
          font-size: 13px;
          font-weight: 800;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 9px;
          transition: all 0.35s ease;
        }

        .primary-button {
          background: linear-gradient(135deg, #00bce9, #008cb7);
          border: none;
          color: #03101c;
        }

        .primary-button:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 30px rgba(0,190,235,0.3);
        }

        .secondary-button {
          background: transparent;
          color: white;
          border: 1px solid #31556b;
        }

        .secondary-button:hover {
          border-color: #ff772d;
          color: #ff772d;
          transform: translateY(-4px);
        }

        .hero-right {
          position: relative;
          min-height: 600px;
          display: flex;
          align-items: center;
          justify-content: center;
          animation: heroImage 1.1s ease forwards;
        }

        .hero-image-frame {
          width: 100%;
          height: 650px;
          position: relative;
          overflow: hidden;
          border-radius: 25px 0 0 25px;
          border: 1px solid rgba(0, 194, 239, 0.25);
          box-shadow:
            -30px 30px 80px rgba(0,0,0,0.4),
            0 0 60px rgba(0, 170, 220, 0.08);
        }

        .hero-image-frame::after {
          content: "";
          position: absolute;
          inset: 0;
          background:
            linear-gradient(
              90deg,
              rgba(5,19,33,0.45),
              transparent 40%
            );
          pointer-events: none;
        }

        .hero-image-frame img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 1.2s ease;
        }

        .hero-image-frame:hover img {
          transform: scale(1.04);
        }

        .hero-floating-card {
          position: absolute;
          bottom: 35px;
          left: -45px;
          width: 310px;
          padding: 22px;
          border-radius: 12px;
          background: rgba(5,22,38,0.92);
          backdrop-filter: blur(15px);
          border: 1px solid rgba(0,190,235,0.35);
          box-shadow: 0 20px 50px rgba(0,0,0,0.4);
          z-index: 3;
          animation: floatingCard 5s ease-in-out infinite;
        }

        .hero-floating-card h3 {
          margin: 0 0 8px;
          font-size: 17px;
          color: white;
        }

        .hero-floating-card p {
          margin: 0;
          color: #9db5c6;
          font-size: 12px;
          line-height: 1.6;
        }

        .hero-scroll {
          position: absolute;
          bottom: 25px;
          left: 50%;
          transform: translateX(-50%);
          color: #60859c;
          font-size: 11px;
          letter-spacing: 3px;
          text-transform: uppercase;
          z-index: 5;
        }

        .hero-scroll span {
          display: block;
          width: 1px;
          height: 40px;
          margin: 8px auto 0;
          background: linear-gradient(#00bce9, transparent);
          animation: scrollLine 1.8s infinite;
        }

        /* =========================================================
           SERVICES
        ========================================================= */

        .services-section {
          background:
            linear-gradient(
              180deg,
              #071a2b,
              #061321
            );
        }

        .services-grid {
          display: grid;
          grid-template-columns: repeat(6, 1fr);
          gap: 1px;
          background: #294052;
          border: 1px solid #294052;
        }

        .service-card {
          background:
            linear-gradient(
              145deg,
              #092238,
              #061522
            );
          padding: 35px 18px;
          min-height: 280px;
          position: relative;
          overflow: hidden;
          transition: all 0.4s ease;
        }

        .service-card::before {
          content: "";
          position: absolute;
          width: 100%;
          height: 3px;
          left: 0;
          top: 0;
          background: linear-gradient(90deg, #00bce9, #ff762d);
          transform: scaleX(0);
          transform-origin: left;
          transition: transform 0.4s ease;
        }

        .service-card:hover {
          transform: translateY(-10px);
          background: #0a2940;
          z-index: 2;
        }

        .service-card:hover::before {
          transform: scaleX(1);
        }

        .service-icon {
          font-size: 36px;
          color: #00bce9;
          margin-bottom: 22px;
          transition: all 0.4s ease;
        }

        .service-card:nth-child(3n) .service-icon {
          color: #ff762d;
        }

        .service-card:hover .service-icon {
          transform: scale(1.15) rotate(-5deg);
        }

        .service-card h3 {
          font-size: 14px;
          margin: 0 0 13px;
          line-height: 1.35;
          text-transform: uppercase;
        }

        .service-card p {
          color: #8fa8b8;
          font-size: 12px;
          line-height: 1.7;
          margin: 0;
        }

        /* =========================================================
           ERP SECTION
        ========================================================= */

        .erp-section {
          background:
            radial-gradient(
              circle at 75% 50%,
              rgba(0,188,235,0.12),
              transparent 32%
            ),
            #061321;
        }

        .erp-layout {
          display: grid;
          grid-template-columns: 35% 65%;
          gap: 50px;
          align-items: center;
        }

        .erp-copy h2 {
          margin: 0;
          font-size: clamp(32px, 4vw, 54px);
          line-height: 1;
          text-transform: uppercase;
        }

        .erp-copy h2 span {
          color: #00bce9;
        }

        .erp-copy .erp-orange {
          display: block;
          color: #ff762d;
          font-size: 18px;
          margin-top: 14px;
          letter-spacing: 2px;
        }

        .erp-copy p {
          color: #9db3c2;
          line-height: 1.8;
          font-size: 14px;
          margin-top: 22px;
        }

        .erp-benefits {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 10px;
          margin-top: 25px;
        }

        .erp-benefit {
          padding: 12px;
          border: 1px solid #28455b;
          background: rgba(8,31,50,0.7);
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 11px;
          font-weight: 700;
          transition: 0.3s ease;
        }

        .erp-benefit:hover {
          border-color: #00bce9;
          transform: translateX(5px);
        }

        .erp-benefit svg {
          color: #ff762d;
        }

        .erp-diagram {
          position: relative;
          min-height: 500px;
          display: flex;
          justify-content: center;
          align-items: center;
        }

        .erp-center {
          width: 220px;
          height: 220px;
          border-radius: 50%;
          border: 3px solid #00bce9;
          display: flex;
          align-items: center;
          justify-content: center;
          text-align: center;
          background:
            radial-gradient(
              circle,
              rgba(0,188,235,0.16),
              rgba(4,20,35,0.95)
            );
          box-shadow:
            0 0 0 12px rgba(0,188,235,0.04),
            0 0 50px rgba(0,188,235,0.15);
          z-index: 3;
          animation: pulseCircle 3s infinite;
        }

        .erp-center h3 {
          margin: 0;
          font-size: 34px;
          color: #00bce9;
          line-height: 0.9;
        }

        .erp-center span {
          display: block;
          color: white;
          font-size: 13px;
          margin-top: 8px;
          letter-spacing: 2px;
        }

        .erp-node {
          position: absolute;
          width: 190px;
          min-height: 70px;
          padding: 13px 15px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          gap: 12px;
          border: 1px solid;
          backdrop-filter: blur(8px);
          transition: all 0.35s ease;
        }

        .erp-node:hover {
          transform: scale(1.06);
        }

        .erp-node svg {
          font-size: 25px;
          flex-shrink: 0;
        }

        .erp-node strong {
          font-size: 11px;
          line-height: 1.3;
        }

        .erp-node.finance {
          top: 30px;
          left: 3%;
          color: #61c8ff;
          border-color: #2088bd;
          background: rgba(20,83,118,0.4);
        }

        .erp-node.sales {
          top: 30px;
          right: 3%;
          color: #ff9b50;
          border-color: #b76527;
          background: rgba(105,57,25,0.4);
        }

        .erp-node.inventory {
          top: 150px;
          left: 0;
          color: #30e1cb;
          border-color: #1a9e91;
          background: rgba(20,106,98,0.35);
        }

        .erp-node.purchase {
          top: 150px;
          right: 0;
          color: #b3e949;
          border-color: #6f9e23;
          background: rgba(75,106,28,0.35);
        }

        .erp-node.hr {
          bottom: 80px;
          left: 4%;
          color: #d279ff;
          border-color: #8d43ae;
          background: rgba(92,45,117,0.35);
        }

        .erp-node.reports {
          bottom: 80px;
          right: 4%;
          color: #62c7ff;
          border-color: #217ba8;
          background: rgba(22,75,106,0.4);
        }

        /* =========================================================
           PROCESS
        ========================================================= */

        .process-section {
          background: #071b2c;
        }

        .process-grid {
          display: grid;
          grid-template-columns: repeat(5, 1fr);
          position: relative;
        }

        .process-grid::before {
          content: "";
          position: absolute;
          left: 10%;
          right: 10%;
          top: 55px;
          height: 1px;
          background: linear-gradient(
            90deg,
            #00bce9,
            #ff762d,
            #00bce9
          );
        }

        .process-item {
          text-align: center;
          position: relative;
          z-index: 2;
        }

        .process-icon {
          width: 90px;
          height: 90px;
          border-radius: 50%;
          margin: auto;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #071a2a;
          border: 2px solid #00bce9;
          color: #00bce9;
          font-size: 30px;
          transition: 0.4s ease;
        }

        .process-item:nth-child(even) .process-icon {
          border-color: #ff762d;
          color: #ff762d;
        }

        .process-item:hover .process-icon {
          transform: translateY(-10px) scale(1.08);
          box-shadow: 0 15px 30px rgba(0,188,235,0.15);
        }

        .process-item h3 {
          margin: 20px 0 8px;
          font-size: 13px;
          text-transform: uppercase;
        }

        .process-item p {
          margin: auto;
          max-width: 160px;
          color: #8da7b8;
          font-size: 11px;
          line-height: 1.6;
        }

        /* =========================================================
           WHY CHOOSE
        ========================================================= */

        .why-section {
          background: #061321;
        }

        .why-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 40px;
          align-items: stretch;
        }

        .why-box {
          border: 1px solid #28465c;
          padding: 38px;
          background:
            linear-gradient(
              145deg,
              rgba(8,34,54,0.8),
              rgba(5,19,32,0.9)
            );
          position: relative;
          overflow: hidden;
        }

        .why-box::after {
          content: "";
          position: absolute;
          width: 180px;
          height: 180px;
          border-radius: 50%;
          right: -80px;
          bottom: -80px;
          background: rgba(0,188,235,0.08);
        }

        .why-box h3 {
          margin: 0 0 25px;
          font-size: 25px;
          text-transform: uppercase;
        }

        .check-list {
          display: flex;
          flex-direction: column;
          gap: 17px;
        }

        .check-row {
          display: flex;
          gap: 12px;
          color: #c1d0db;
          font-size: 14px;
          align-items: flex-start;
        }

        .check-row svg {
          color: #00c2eb;
          margin-top: 2px;
          flex-shrink: 0;
        }

        .why-stats {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 15px;
        }

        .stat-box {
          padding: 25px;
          border: 1px solid #28465c;
          background: #081d30;
          transition: 0.35s ease;
        }

        .stat-box:hover {
          transform: translateY(-7px);
          border-color: #ff762d;
        }

        .stat-box svg {
          font-size: 28px;
          color: #ff762d;
          margin-bottom: 12px;
        }

        .stat-box strong {
          display: block;
          font-size: 13px;
          text-transform: uppercase;
          margin-bottom: 7px;
        }

        .stat-box span {
          color: #819aab;
          font-size: 11px;
          line-height: 1.5;
        }

        /* =========================================================
           QUOTE BANNER
        ========================================================= */

        .quote-banner {
          width: 100%;
          padding: 55px 5vw;
          background:
            linear-gradient(
              90deg,
              #062139,
              #07192b,
              #062139
            );
          border-top: 1px solid #23465e;
          border-bottom: 1px solid #23465e;
        }

        .quote-inner {
          max-width: 1450px;
          margin: auto;
          display: grid;
          grid-template-columns: 1.5fr 1fr;
          align-items: center;
          gap: 40px;
        }

        .quote-content {
          display: flex;
          gap: 15px;
          align-items: flex-start;
        }

        .quote-content svg {
          color: #ff762d;
          font-size: 28px;
          flex-shrink: 0;
        }

        .quote-content h2 {
          margin: 0;
          font-size: clamp(20px, 3vw, 35px);
          line-height: 1.25;
          text-transform: uppercase;
        }

        .quote-content h2 span {
          color: #00bce9;
        }

        .quote-content h2 em {
          color: #ff762d;
          font-style: normal;
        }

        .quote-points {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 12px;
        }

        .quote-point {
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 11px;
          color: #a9c0cf;
        }

        .quote-point svg {
          color: #00bce9;
          font-size: 20px;
        }

        /* =========================================================
           FOOTER
        ========================================================= */

        .footer {
          background: #f0f2f4;
          color: #15263a;
          width: 100%;
        }

        .footer-inner {
          max-width: 1450px;
          margin: auto;
          min-height: 145px;
          display: grid;
          grid-template-columns: 150px 1.5fr 2fr;
          align-items: center;
        }

        .footer-number {
          height: 145px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #16a9c4;
          color: white;
          font-size: 60px;
          font-weight: 900;
          clip-path: polygon(
            0 0,
            100% 0,
            82% 100%,
            0 100%
          );
        }

        .footer-company {
          padding: 20px 35px;
        }

        .footer-company h3 {
          margin: 0;
          font-size: 19px;
          color: #173352;
        }

        .footer-company p {
          margin: 8px 0 0;
          color: #2197b1;
          font-size: 14px;
        }

        .footer-pillars {
          display: grid;
          grid-template-columns: repeat(5, 1fr);
          height: 100%;
        }

        .footer-pillar {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          border-left: 1px solid #c4ccd2;
          padding: 15px 8px;
          transition: 0.3s ease;
        }

        .footer-pillar:hover {
          background: #e5eaee;
        }

        .footer-pillar svg {
          color: #1b425f;
          font-size: 25px;
          margin-bottom: 8px;
        }

        .footer-pillar span {
          font-size: 8px;
          font-weight: 800;
          line-height: 1.35;
        }

        /* =========================================================
           ANIMATIONS
        ========================================================= */

        @keyframes heroText {
          from {
            opacity: 0;
            transform: translateX(-50px);
          }

          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes heroImage {
          from {
            opacity: 0;
            transform: translateX(50px);
          }

          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes floatingCard {
          0%,100% {
            transform: translateY(0);
          }

          50% {
            transform: translateY(-10px);
          }
        }

        @keyframes floatGlow {
          0%,100% {
            transform: translate(0,0);
          }

          50% {
            transform: translate(-30px,30px);
          }
        }

        @keyframes pulseCircle {
          0%,100% {
            box-shadow:
              0 0 0 12px rgba(0,188,235,0.04),
              0 0 50px rgba(0,188,235,0.15);
          }

          50% {
            box-shadow:
              0 0 0 24px rgba(0,188,235,0.02),
              0 0 80px rgba(0,188,235,0.25);
          }
        }

        @keyframes scrollLine {
          0% {
            opacity: 0;
            transform: translateY(-5px);
          }

          50% {
            opacity: 1;
          }

          100% {
            opacity: 0;
            transform: translateY(10px);
          }
        }

        /* =========================================================
           TABLET
        ========================================================= */

        @media (max-width: 1100px) {

          .hero-content {
            grid-template-columns: 1fr;
            gap: 45px;
          }

          .hero-left {
            padding-right: 0;
            max-width: 850px;
          }

          .hero-right {
            min-height: auto;
          }

          .hero-image-frame {
            height: 520px;
            border-radius: 18px;
          }

          .services-grid {
            grid-template-columns: repeat(3, 1fr);
          }

          .erp-layout {
            grid-template-columns: 1fr;
          }

          .erp-copy {
            max-width: 750px;
          }

          .erp-diagram {
            min-height: 600px;
          }

          .process-grid {
            grid-template-columns: repeat(3, 1fr);
            gap: 45px 20px;
          }

          .process-grid::before {
            display: none;
          }

          .why-grid {
            grid-template-columns: 1fr;
          }

          .quote-inner {
            grid-template-columns: 1fr;
          }

          .footer-inner {
            grid-template-columns: 110px 1fr;
          }

          .footer-pillars {
            grid-column: 1 / -1;
            min-height: 120px;
          }

          .footer-number {
            height: 120px;
          }
        }

        /* =========================================================
           MOBILE
        ========================================================= */

        @media (max-width: 700px) {

          .top-navigation {
            top: 12px;
            left: 12px;
          }

          .back-button {
            padding: 9px 13px;
            font-size: 11px;
          }

          .section-container {
            padding: 60px 18px;
          }

          .hero {
            min-height: auto;
          }

          .hero-content {
            padding: 95px 18px 60px;
            gap: 35px;
          }

          .logo-wrapper {
            width: 280px;
            margin-left: auto;
            margin-right: auto;
            margin-bottom: 7px;
          }

          .hero-left {
            text-align: center;
          }

          /* Mobile logo name */

          .technology-name {
            font-size: 15px;
            letter-spacing: 3px;
            margin-top: 3px;
            margin-bottom: 24px;
            padding-left: 0;
          }

          .technology-name::after {
            margin-left: auto;
            margin-right: auto;
            width: 55px;
            height: 2px;
          }

          .hero-kicker {
            justify-content: center;
            font-size: 11px;
          }

          .hero-title {
            font-size: 39px;
          }

          .hero-description {
            font-size: 13px;
            line-height: 1.7;
          }

          .hero-buttons {
            justify-content: center;
          }

          .hero-right {
            width: 100%;
          }

          .hero-image-frame {
            height: 400px;
            border-radius: 14px;
          }

          .hero-floating-card {
            left: 15px;
            right: 15px;
            bottom: 15px;
            width: auto;
            padding: 16px;
          }

          .hero-floating-card h3 {
            font-size: 14px;
          }

          .hero-floating-card p {
            font-size: 10px;
          }

          .hero-scroll {
            display: none;
          }

          .services-grid {
            grid-template-columns: 1fr;
          }

          .service-card {
            min-height: auto;
            padding: 28px 22px;
          }

          .service-icon {
            font-size: 30px;
          }

          .erp-copy h2 {
            font-size: 38px;
          }

          .erp-diagram {
            min-height: 750px;
          }

          .erp-center {
            width: 170px;
            height: 170px;
          }

          .erp-center h3 {
            font-size: 26px;
          }

          .erp-node {
            width: 145px;
            min-height: 62px;
            padding: 10px;
          }

          .erp-node strong {
            font-size: 9px;
          }

          .erp-node svg {
            font-size: 20px;
          }

          .erp-node.finance {
            top: 30px;
            left: 0;
          }

          .erp-node.sales {
            top: 30px;
            right: 0;
          }

          .erp-node.inventory {
            top: 180px;
            left: 0;
          }

          .erp-node.purchase {
            top: 180px;
            right: 0;
          }

          .erp-node.hr {
            bottom: 150px;
            left: 0;
          }

          .erp-node.reports {
            bottom: 150px;
            right: 0;
          }

          .erp-benefits {
            grid-template-columns: 1fr;
          }

          .process-grid {
            grid-template-columns: 1fr;
            gap: 35px;
          }

          .process-icon {
            width: 75px;
            height: 75px;
          }

          .why-box {
            padding: 25px 20px;
          }

          .why-stats {
            grid-template-columns: 1fr;
          }

          .quote-banner {
            padding: 45px 18px;
          }

          .quote-content h2 {
            font-size: 20px;
          }

          .quote-points {
            grid-template-columns: 1fr 1fr;
          }

          .footer-inner {
            grid-template-columns: 80px 1fr;
          }

          .footer-number {
            height: 105px;
            font-size: 40px;
          }

          .footer-company {
            padding: 15px;
          }

          .footer-company h3 {
            font-size: 13px;
          }

          .footer-company p {
            font-size: 10px;
          }

          .footer-pillars {
            grid-template-columns: repeat(5, 1fr);
            min-height: 95px;
          }

          .footer-pillar {
            padding: 8px 2px;
          }

          .footer-pillar svg {
            font-size: 17px;
          }

          .footer-pillar span {
            font-size: 6px;
          }
        }

        /* =========================================================
           SMALL MOBILE
        ========================================================= */

        @media (max-width: 420px) {

          .hero-title {
            font-size: 33px;
          }

          .logo-wrapper {
            width: 240px;
          }

          .technology-name {
            font-size: 13px;
            letter-spacing: 2.5px;
            margin-bottom: 22px;
          }

          .hero-image-frame {
            height: 330px;
          }

          .erp-diagram {
            transform: scale(0.92);
            margin-left: -15px;
            margin-right: -15px;
          }

          .quote-points {
            grid-template-columns: 1fr;
          }

          .footer-company h3 {
            font-size: 11px;
          }

          .footer-company p {
            font-size: 8px;
          }
        }

      `}</style>


      <div className="tech-page">

        {/* =======================================================
            BACK NAVIGATION
        ======================================================= */}

        <div className="top-navigation">

          <button
            className="back-button"
            onClick={() => navigate('/')}
          >
            <FaArrowLeft />
            Back to All Sectors
          </button>

        </div>


        {/* =======================================================
            HERO
        ======================================================= */}

        <section className="hero">

          <div className="hero-content">

            <div className="hero-left">

              {/* =================================================
                  LOGO
              ================================================= */}

              <div className="logo-wrapper">

                <img
                  src={ashrafiaLogo}
                  alt="Ashrafia Technologies Logo"
                />

              </div>


              {/* =================================================
                  COMPANY NAME BELOW LOGO
              ================================================= */}

              <div className="technology-name">

                <span>ASHRAF</span> TECHNOLOGIES

              </div>


              <div className="hero-kicker">

                Digital Today. Intelligent Tomorrow.

              </div>


              <h1 className="hero-title">

                DIGITAL GROWTH

                <span className="blue">
                  DRIVEN BY
                </span>

                <span className="orange">
                  STRATEGY.
                </span>

              </h1>


              <p className="hero-description">

                Ashrafia Technologies is the digital arm of
                <strong> Ashraf Group</strong>, driving brands
                forward in the digital world.

                We specialize in Digital Marketing and are
                expanding our expertise into
                <strong> ERP Solutions</strong> — empowering
                businesses to grow, automate and succeed.

              </p>


              <div className="hero-buttons">

                <button
                  className="primary-button"
                  onClick={() =>
                    document
                      .getElementById('services')
                      ?.scrollIntoView()
                  }
                >

                  Explore Services

                  <FaArrowRight />

                </button>


                <button
                  className="secondary-button"
                  onClick={() =>
                    document
                      .getElementById('erp')
                      ?.scrollIntoView()
                  }
                >

                  ERP Solutions

                  <FaCogs />

                </button>

              </div>

            </div>


            {/* ===================================================
                HERO IMAGE
            =================================================== */}

            <div className="hero-right">

              <div className="hero-image-frame">

                <img
                  src={heroImage}
                  alt="Ashrafia Technologies Digital Growth"
                />

              </div>


              <div className="hero-floating-card">

                <h3>

                  <span className="cyan">
                    DIGITAL
                  </span>{' '}

                  TRANSFORMATION

                </h3>


                <p>

                  Strategy, technology and creativity
                  working together to create measurable
                  business growth.

                </p>

              </div>

            </div>

          </div>


          <div className="hero-scroll">

            Scroll to explore

            <span />

          </div>

        </section>


        {/* =======================================================
            DIGITAL MARKETING SERVICES
        ======================================================= */}

        <section
          id="services"
          className="section-container services-section"
        >

          <div className="section-inner">

            <div className="section-heading reveal">

              <div className="section-heading-small">

                Our Digital Marketing Services

              </div>


              <h2>

                BUILDING <span>DIGITAL</span> GROWTH

              </h2>


              <p>

                Powerful digital strategies designed to
                strengthen your brand, increase visibility,
                engage customers and create sustainable growth.

              </p>

            </div>


            <div className="services-grid">

              <ServiceCard
                icon={<FaBullhorn />}
                title="Social Media Marketing"
                text="Engaging content and targeted campaigns that build brand awareness and customer loyalty."
              />

              <ServiceCard
                icon={<FaSearch />}
                title="Search Engine Optimization"
                text="Rank higher, get found faster and drive sustainable organic growth."
              />

              <ServiceCard
                icon={<FaChartLine />}
                title="Pay Per Click"
                text="Maximize ROI with targeted advertising campaigns designed to deliver measurable results."
              />

              <ServiceCard
                icon={<FaFileInvoiceDollar />}
                title="Content Marketing"
                text="Powerful content that informs, inspires and converts your audience."
              />

              <ServiceCard
                icon={<FaLaptopCode />}
                title="Branding & Design"
                text="Creative digital experiences that communicate your brand's true identity."
              />

              <ServiceCard
                icon={<FaChartBar />}
                title="Analytics & Reporting"
                text="Data-driven insights that measure performance and fuel better decisions."
              />

            </div>

          </div>

        </section>


        {/* =======================================================
            ERP SECTION
        ======================================================= */}

        <section
          id="erp"
          className="section-container erp-section"
        >

          <div className="section-inner">

            <div className="erp-layout">

              <div className="erp-copy reveal-left">

                <div className="section-heading-small">

                  Expanding Our Expertise

                </div>


                <h2>

                  ERP

                  <span>
                    SOLUTIONS
                  </span>

                </h2>


                <span className="erp-orange">

                  INTEGRATE. AUTOMATE. ELEVATE.

                </span>


                <p>

                  We are stepping into the future with ERP
                  Solutions that streamline operations,
                  integrate departments and provide
                  real-time visibility for better decisions
                  and faster growth.

                </p>


                <div className="erp-benefits">

                  <div className="erp-benefit">

                    <FaBullseye />

                    Improve Efficiency

                  </div>


                  <div className="erp-benefit">

                    <FaCogs />

                    Reduce Costs

                  </div>


                  <div className="erp-benefit">

                    <FaShieldAlt />

                    Enhance Accuracy

                  </div>


                  <div className="erp-benefit">

                    <FaChartLine />

                    Drive Growth

                  </div>

                </div>

              </div>


              <div className="erp-diagram reveal-right">

                <div className="erp-center">

                  <div>

                    <h3>
                      ERP
                    </h3>

                    <span>
                      SOLUTIONS
                    </span>

                  </div>

                </div>


                <div className="erp-node finance">

                  <FaFileInvoiceDollar />

                  <strong>
                    FINANCE &<br />
                    ACCOUNTING
                  </strong>

                </div>


                <div className="erp-node sales">

                  <FaChartBar />

                  <strong>
                    SALES & CRM
                  </strong>

                </div>


                <div className="erp-node inventory">

                  <FaBoxes />

                  <strong>
                    INVENTORY<br />
                    MANAGEMENT
                  </strong>

                </div>


                <div className="erp-node purchase">

                  <FaShoppingCart />

                  <strong>
                    PURCHASE<br />
                    MANAGEMENT
                  </strong>

                </div>


                <div className="erp-node hr">

                  <FaUserTie />

                  <strong>
                    HR & PAYROLL
                  </strong>

                </div>


                <div className="erp-node reports">

                  <FaChartLine />

                  <strong>
                    REPORTS &<br />
                    ANALYTICS
                  </strong>

                </div>

              </div>

            </div>

          </div>

        </section>


        {/* =======================================================
            WORK PROCESS
        ======================================================= */}

        <section className="section-container process-section">

          <div className="section-inner">

            <div className="section-heading reveal">

              <div className="section-heading-small">

                Our Work Process

              </div>


              <h2>

                FROM <span>IDEA</span> TO IMPACT

              </h2>

            </div>


            <div className="process-grid">

              <ProcessItem
                icon={<FaSearch />}
                title="Discover"
                text="Understand your goals, challenges and opportunities."
              />

              <ProcessItem
                icon={<FaChessKnight />}
                title="Strategize"
                text="Build a customized plan designed for success."
              />

              <ProcessItem
                icon={<FaCog />}
                title="Execute"
                text="Implement every strategy with precision and care."
              />

              <ProcessItem
                icon={<FaChartLine />}
                title="Analyze"
                text="Track performance, measure results and optimize."
              />

              <ProcessItem
                icon={<FaRocket />}
                title="Grow"
                text="Scale successful strategies and achieve more."
              />

            </div>

          </div>

        </section>


        {/* =======================================================
            WHY CHOOSE US
        ======================================================= */}

        <section className="section-container why-section">

          <div className="section-inner">

            <div className="section-heading reveal">

              <div className="section-heading-small">

                Why Choose Ashrafia Technologies?

              </div>


              <h2>

                BUILT FOR <span>BUSINESS</span> GROWTH

              </h2>

            </div>


            <div className="why-grid">

              <div className="why-box reveal-left">

                <h3>

                  Why <span className="cyan">
                    Ashrafia
                  </span>?

                </h3>


                <div className="check-list">

                  <CheckRow text="Experienced & passionate team" />

                  <CheckRow text="Creative strategies tailored to your business" />

                  <CheckRow text="Results-driven approach with measurable impact" />

                  <CheckRow text="Transparent communication & timely delivery" />

                  <CheckRow text="Commitment to innovation and growth" />

                  <CheckRow text="Digital marketing + ERP expertise" />

                </div>

              </div>


              <div className="why-stats reveal-right">

                <StatBox
                  icon={<FaLightbulb />}
                  title="Innovative Solutions"
                  text="Modern strategies built around your business objectives."
                />

                <StatBox
                  icon={<FaUsers />}
                  title="Client Focused"
                  text="Long-term partnerships based on trust and collaboration."
                />

                <StatBox
                  icon={<FaRocket />}
                  title="Future Ready"
                  text="Technology designed to evolve with your business."
                />

                <StatBox
                  icon={<FaHandshake />}
                  title="Together We Grow"
                  text="Your success remains at the center of everything we do."
                />

              </div>

            </div>

          </div>

        </section>


        {/* =======================================================
            QUOTE
        ======================================================= */}

        <section className="quote-banner">

          <div className="quote-inner">

            <div className="quote-content reveal-left">

              <FaQuoteLeft />

              <h2>

                WE DON'T JUST MARKET BRANDS,

                <br />

                WE BUILD

                <span>
                  DIGITAL GROWTH
                </span>

                <em>
                  STORIES.
                </em>

              </h2>

              <FaQuoteRight />

            </div>


            <div className="quote-points reveal-right">

              <div className="quote-point">

                <FaLightbulb />

                Innovative Solutions

              </div>


              <div className="quote-point">

                <FaUsers />

                Client Focused

              </div>


              <div className="quote-point">

                <FaRocket />

                Future Ready

              </div>


              <div className="quote-point">

                <FaHandshake />

                Together We Grow

              </div>

            </div>

          </div>

        </section>


        {/* =======================================================
            FOOTER
        ======================================================= */}

        <footer className="footer">

          <div className="footer-inner">

            <div className="footer-number">
              24
            </div>


            <div className="footer-company">

              <h3>
                ASHRAF GROUP OF INDUSTRIES
              </h3>

              <p>
                Building Legacy. Powering the Future.
              </p>

            </div>


            <div className="footer-pillars">

              <FooterPillar
                icon={<FaBuilding />}
                text={
                  <>
                    DIVERSE
                    <br />
                    PORTFOLIO
                  </>
                }
              />


              <FooterPillar
                icon={<FaGlobe />}
                text={
                  <>
                    GLOBAL
                    <br />
                    PRESENCE
                  </>
                }
              />


              <FooterPillar
                icon={<FaShieldAlt />}
                text={
                  <>
                    TRUSTED
                    <br />
                    EXCELLENCE
                  </>
                }
              />


              <FooterPillar
                icon={<FaHandshake />}
                text={
                  <>
                    DELIVERING
                    <br />
                    VALUE
                  </>
                }
              />


              <FooterPillar
                icon={<FaChartLine />}
                text={
                  <>
                    GROWING
                    <br />
                    TOGETHER
                  </>
                }
              />

            </div>

          </div>

        </footer>

      </div>
    </>
  );
}


/* =============================================================
   SERVICE CARD
============================================================= */

function ServiceCard({ icon, title, text }) {

  return (

    <div className="service-card reveal">

      <div className="service-icon">
        {icon}
      </div>

      <h3>
        {title}
      </h3>

      <p>
        {text}
      </p>

    </div>

  );

}


/* =============================================================
   PROCESS ITEM
============================================================= */

function ProcessItem({ icon, title, text }) {

  return (

    <div className="process-item reveal">

      <div className="process-icon">
        {icon}
      </div>

      <h3>
        {title}
      </h3>

      <p>
        {text}
      </p>

    </div>

  );

}


/* =============================================================
   CHECK ROW
============================================================= */

function CheckRow({ text }) {

  return (

    <div className="check-row">

      <FaCheckCircle />

      <span>
        {text}
      </span>

    </div>

  );

}


/* =============================================================
   STAT BOX
============================================================= */

function StatBox({ icon, title, text }) {

  return (

    <div className="stat-box reveal">

      {icon}

      <strong>
        {title}
      </strong>

      <span>
        {text}
      </span>

    </div>

  );

}


/* =============================================================
   FOOTER PILLAR
============================================================= */

function FooterPillar({ icon, text }) {

  return (

    <div className="footer-pillar">

      {icon}

      <span>
        {text}
      </span>

    </div>

  );

}    